import React, { useEffect, useState } from "react";
import Dropdown from "../common/dropdown";
import TextInput from "../common/textinput";
import Textarea from "../common/textarea";
import { yesNo } from "../../constants/dropdown/casing";
import { useAppDispatch, useAppSelector } from "../../store/hooks/StoreHooks";
import useOthersFacade from "../../facade/calculation/calculation-input/othersTab.facade";
import { transformObjectToDropdownOptions } from "../../utilities/commonUtilities";
import useCollapseFacade from "../../facade/collapse.Facade";
import Accordion from "../common/accordion";

const Others: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    updateSupplyPowerList,
    updateControllerList,
    updateSupplyPower,
    updateSupplyVoltage,
    updateAvailableKVA,
    updateController,
    updateOxygen,
    updateNitrogen,
    updateH2S,
    updateCO2,
    updateCorrosion,
    updateParaffin,
    updateScale,
    updateSandSolids,
    updateViscosityEmulsion,
    updatePacker,
    updateChemical,
    updateYTool,
    updateScdaCompatibility,
    updatePressureSensor,
    updateNotes,
  } = useOthersFacade();

  const { loading } = useAppSelector((state) => state?.others);
  const {
    tab5Input,
    supplyPowerValue,
    supplyVoltageValue,
    availableKVAValue,
    controllerValue,
    oxygenValue,
    nitrogenValue,
    h2sValue,
    co2Value,
    corrosionValue,
    paraffinValue,
    scaleValue,
    sandSolidsValue,
    viscocityEmulsionValue,
    packerValue,
    chemicalValue,
    yToolValue,
    scdaCompatibilityValue,
    pressureSensorValue,
    notesValue,
    supplyPowerListValue,
    controllerListValue,
  } = useAppSelector((state) => state?.others);

  const { projectDetails } = useAppSelector((state) => state?.calculations);

  const handleSelect = (option: any, type: "Supply Power" | "Controller") => {
    if (type === "Supply Power") {
      updateSupplyPower(option?.id);
    } else if (type === "Controller") {
      updateController(option?.id);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    const updateFunctions: Partial<Record<string, any>> = {
      supplyVoltage: updateSupplyVoltage,
      availableKVA: updateAvailableKVA,
      oxygen: updateOxygen,
      nitrogen: updateNitrogen,
      h2s: updateH2S,
      co2: updateCO2,
    };
    const isNumberField = [
      "supplyVoltage",
      "availableKVA",
      "oxygen",
      "nitrogen",
      "h2s",
      "co2",
    ]?.includes(name);
    const numberRegex = /^(?:\d+(\.\d*)?|\.\d+)?$/;
    const action = updateFunctions[name];
    if (value === "" || (numberRegex.test(value) && isNumberField)) {
      if (action) {
        dispatch(action(value));
      }
    }
  };

  const {
    updatePowerConditionsCollapse,
    updateGasImpuritiesCollapse,
    updateProblemsCollapse,
    updateOthersTabCollapse,
    updateNotesCollapse,
  } = useCollapseFacade();

  const {
    powerConditionsCollapse,
    gasImpuritiesCollapse,
    problemsCollapse,
    othersTabCollapse,
    notesCollapse,
  } = useAppSelector((state) => state?.collapse);

  const handleCollapseChange = (key: boolean, name: string) => {
    if (name === "powerConditions") updatePowerConditionsCollapse(key);
    else if (name === "gasImpurities") updateGasImpuritiesCollapse(key);
    else if (name === "problems") updateProblemsCollapse(key);
    else if (name === "others") updateOthersTabCollapse(key);
    else if (name === "notes") updateNotesCollapse(key);
  };

  return (
    <>
      <div className="flex flex-col gap-[16px]">
        {/* Power Conditions */}
        <Accordion
          title="Power conditions"
          isOpen={powerConditionsCollapse}
          onToggle={() =>
            handleCollapseChange(!powerConditionsCollapse, "powerConditions")
          }
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              <Dropdown
                options={tab5Input?.unitSuplPwrList}
                placeholder="Select"
                label="Supply power"
                onSelect={(option) => updateSupplyPower(option?.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.unitSuplPwrListVal}
                showSelectOption
              />
              <TextInput
                name="supplyVoltage"
                value={tab5Input?.suplVlt || ""}
                onChange={(e) => handleChange(e)}
                placeholder="Enter Supply Voltage"
                label="Supply voltage / Hz"
              />
              <TextInput
                name="availableKVA"
                value={tab5Input?.availKVA?.toString() || ""}
                onChange={(e) => handleChange(e)}
                placeholder="Enter Available KVA"
                label="Available KVA"
              />
              <Dropdown
                options={tab5Input?.unitContrlList}
                placeholder="Select"
                label="Controller"
                onSelect={(option) => handleSelect(option, "Controller")}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.unitContrlListVal}
                showSelectOption
              />
            </div>
          }
        />
        {/* Gas Impurities */}
        <Accordion
          title="Gas impurities"
          isOpen={gasImpuritiesCollapse}
          onToggle={() =>
            handleCollapseChange(!gasImpuritiesCollapse, "gasImpurities")
          }
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              <TextInput
                name="oxygen"
                value={tab5Input?.oxygenVal || ""}
                onChange={(e) => handleChange(e)}
                placeholder="Enter oxygen"
                unit="%"
                label="Oxygen (O2)"
              />
              <TextInput
                name="nitrogen"
                value={tab5Input?.nitrogenVal || ""}
                onChange={(e) => handleChange(e)}
                placeholder="Enter nitrogen"
                unit="%"
                label="Nitrogen (N2)"
              />
              <TextInput
                name="h2s"
                value={tab5Input?.h2S || ""}
                onChange={(e) => handleChange(e)}
                placeholder="Enter H2S"
                unit="%"
                label="H2S"
              />
              <TextInput
                name="co2"
                value={tab5Input?.cO2 || ""}
                onChange={(e) => handleChange(e)}
                placeholder="Enter CO2"
                unit="%"
                label="CO2"
              />
            </div>
          }
        />
        {/* Problems/Concerns */}
        <Accordion
          title="Problems / Concerns"
          isOpen={problemsCollapse}
          onToggle={() => handleCollapseChange(!problemsCollapse, "problems")}
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              <Dropdown
                options={yesNo}
                placeholder="Select corrosion"
                label="Corrosion"
                onSelect={(option) => updateCorrosion(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.unitCorsnListVal}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select paraffin"
                label="Paraffin"
                onSelect={(option) => updateParaffin(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.paraffin}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select scale"
                label="Scale"
                onSelect={(option) => updateScale(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.scale}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select sand / solids"
                label="Sand / Solids"
                onSelect={(option) => updateSandSolids(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.sandSolids}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select viscosity / emulsion"
                label="Viscosity / Emulsion"
                onSelect={(option) => updateViscosityEmulsion(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.viscostyEmulsn}
              />
            </div>
          }
        />
        {/* Others */}
        <Accordion
          title="Others"
          isOpen={othersTabCollapse}
          onToggle={() => handleCollapseChange(!othersTabCollapse, "others")}
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              <Dropdown
                options={yesNo}
                placeholder="Select packer required"
                label="Packer required"
                onSelect={(option) => updatePacker(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.packerReqd}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select chemical"
                label="Chemical"
                onSelect={(option) => updateChemical(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.chemicalYN}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select Y-tool"
                label="Y-tool"
                onSelect={(option) => updateYTool(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.yToolYN}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select SCDA compatibility"
                label="SCDA compatibility"
                onSelect={(option) => updateScdaCompatibility(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.scadayn}
              />
              <Dropdown
                options={yesNo}
                placeholder="Select pressure sensor"
                label="Pressure sensor"
                onSelect={(option) => updatePressureSensor(option.id)}
                bgColor="bg-transparent"
                dropdownBgColor="bg-darkcolor-800"
                selected={tab5Input?.pressureSensYN}
              />
            </div>
          }
        />
        {/* Notes */}
        <Accordion
          title="Notes"
          isOpen={notesCollapse}
          onToggle={() => handleCollapseChange(!notesCollapse, "notes")}
          content={
            <div className="flex flex-col gap-[16px] p-[12px]">
              <Textarea
                value={tab5Input?.additnalNotes || ""}
                onChange={(e) => updateNotes(e.target.value)}
                placeholder="Write here"
                label="Add notes"
                isResizable={false}
                hasOverflow={true}
                rows={2}
                maxLength={100}
              />
            </div>
          }
        />
      </div>
    </>
  );
};

export default Others;
